import { useCallback } from 'react'

import type { one_to_one } from '@fireworkhq/proto-contract'
import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import {
  actionsSelector,
  microphoneMutedSelector,
  paramsSelector,
  useStore,
  visitorInformationSelector,
  visitorPrivateActionsSelector,
} from '@liveHelper/models/store'
import { customLiveHelperEvent } from '@liveHelper/tracking/customLiveHelperEvent'
import { EventType } from '@liveHelper/tracking/eventType'
import { useTrackClickStartCall } from '@liveHelper/tracking/useLiveHelperTracking'

import { useCheckMicrophonePermission } from '@core/devicePermissions/useCheckMicrophonePermission'
import { uuidv4 } from '@core/helpers/uuidv4'
import { useContextTranslation } from '@core/i18n'

import { useChatOnly } from './useChatOnly'

interface useInitiateCallProps {
  shouldTrackClickStartCall?: boolean
}

/**
 * Checks for microphone permission before redirect to request screen or initiating the call
 */
export const useInitiateCall = ({
  shouldTrackClickStartCall = false,
}: useInitiateCallProps) => {
  const { t } = useContextTranslation()
  const params = useStore(paramsSelector)
  const chatChannelConfig = useChatChannelConfig()
  const visitorInformation = useStore(visitorInformationSelector)
  const { callAgent } = useStore(visitorPrivateActionsSelector)
  const microphoneMuted = useStore(microphoneMutedSelector)
  const {
    addChatMessages,
    microphoneMutedStateToggled,
    sendUIStateTransitionEvent,
  } = useStore(actionsSelector)

  const [, checkChatOnly] = useChatOnly()
  const { checkPermissionStatus } = useCheckMicrophonePermission({})
  const trackClickStartCall = useTrackClickStartCall()

  return useCallback(async () => {
    const isChatOnly = await checkChatOnly()
    if (isChatOnly) {
      const message: one_to_one.shared.Message = {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        text_message: {
          content: t(
            'oneToOne:All agents are on calls. Please wait or use the chat option instead.',
          ),
          author: 'AUTHOR_AGENT',
        },
      }
      addChatMessages([message], 'agent')
      return
    }

    if (
      chatChannelConfig?.config?.capture_lead_information &&
      !visitorInformation
    ) {
      sendUIStateTransitionEvent('REQUEST_VISITOR_INFORMATION')
      return
    }

    const permission = await checkPermissionStatus()
    if (permission !== 'granted') {
      sendUIStateTransitionEvent('REQUEST_MICROPHONE_ACCESS')
      if (!microphoneMuted) {
        microphoneMutedStateToggled()
      }
      return
    }

    callAgent?.()
    if (shouldTrackClickStartCall) {
      trackClickStartCall()
      customLiveHelperEvent({
        type: EventType.FW_LIVE_HELPER_CLICK_CALL_AGENT,
        data: {
          widget_id: params?.widget_id,
        },
      })
    }
  }, [
    chatChannelConfig?.config?.capture_lead_information,
    visitorInformation,
    checkChatOnly,
    checkPermissionStatus,
    callAgent,
    shouldTrackClickStartCall,
    sendUIStateTransitionEvent,
    t,
    addChatMessages,
    microphoneMuted,
    microphoneMutedStateToggled,
    trackClickStartCall,
    params?.widget_id,
  ])
}
